import { Link } from "react-router-dom";
import { Reveal, MaskLines } from "../components/Reveal";
import { TEAM } from "../data/team";
import { FILMS } from "../data/films";
import { Mail } from "lucide-react";

// Reshuffle: spread portrait-holders through the list, but never at position 0.
// First portrait lands mid-way through the first "band" so the department opens
// with a monogram card and portrait faces appear in the middle of each row.
function reshuffleDept(members) {
  const withP = members.filter((mm) => mm.portrait);
  const withoutP = members.filter((mm) => !mm.portrait);
  if (withP.length === 0 || withP.length === members.length) return members;
  const total = members.length;
  const spacing = Math.max(1, Math.floor(total / withP.length));
  const start = Math.max(1, Math.floor(spacing / 2)) + (spacing === 1 ? 1 : 0);
  const result = new Array(total);
  let pi = 0;
  const takenSlots = new Set();
  for (let s = start; s < total && pi < withP.length; s += Math.max(spacing, 2)) {
    result[s] = withP[pi++];
    takenSlots.add(s);
  }
  // If any portraits remain (e.g. dense department), place them at the next open slots
  while (pi < withP.length) {
    for (let i = total - 1; i >= 0; i--) {
      if (!takenSlots.has(i)) { result[i] = withP[pi++]; takenSlots.add(i); break; }
    }
  }
  let npi = 0;
  for (let i = 0; i < total; i++) {
    if (!takenSlots.has(i)) result[i] = withoutP[npi++];
  }
  return result;
}

export default function Team() {
  return (
    <main data-testid="team-page" className="bg-black pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">
          Team
        </p>
        <h1 className="font-serif-display font-light text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter">
          <MaskLines lines={["The hands", "on the work."]} />
        </h1>
        <Reveal delay={1} className="mt-10 max-w-xl">
          <p className="text-white/60 leading-relaxed">
            Directors, writers, cinematographers and craftspeople across seven departments —
            forty-plus Nepali and Indian professionals building the studio.
          </p>
        </Reveal>
      </section>

      {Object.entries(TEAM).map(([dept, members], deptIdx) => (
        <section key={dept} className="border-t border-white/10 py-20 md:py-28">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
              <div className="md:col-span-4">
                <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-3">
                  Department {String(deptIdx + 1).padStart(2, "0")}
                </p>
                <h2 className="font-serif-display text-white text-4xl md:text-5xl tracking-tight">
                  {dept}
                </h2>
              </div>
              <div className="md:col-span-8 md:pt-6">
                <div className="h-px bg-white/10 w-full" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {reshuffleDept(members).map((mm, i) => (
                <MemberCard key={mm.name} member={mm} index={i} />
              ))}
            </div>
          </div>
        </section>
      ))}
    </main>
  );
}

function MemberCard({ member, index }) {
  const isTBA = member.name === "To Be Announced";
  const hasBio = !!member.bio;
  return (
    <Reveal
      delay={index}
      className={`group relative border border-white/10 bg-[color:var(--dk-muted)] p-6 flex flex-col justify-between transition-colors hover:border-[color:var(--dk-gold)]/50 ${
        hasBio ? "min-h-[520px] md:col-span-2 lg:col-span-1" : "min-h-[300px]"
      } ${isTBA ? "opacity-60" : ""}`}
      data-testid={`team-member-${member.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {member.portrait ? (
        <div className={`absolute inset-0 overflow-hidden ${hasBio ? "opacity-45 group-hover:opacity-70" : "opacity-30 group-hover:opacity-50"} transition-opacity`}>
          <img
            src={member.portrait}
            alt={member.name}
            className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        </div>
      ) : (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.06),transparent_60%)]" />
          <div className="absolute inset-x-6 top-6 flex items-start justify-between opacity-10">
            <span className="text-5xl font-serif-display italic text-white leading-none">
              {member.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </span>
          </div>
        </div>
      )}

      <div className="relative z-10 flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">
          {String(index + 1).padStart(2, "0")}
        </span>
        {member.film && (
          <span className="text-[10px] uppercase tracking-overline text-white/50 text-right max-w-[60%]">
            {member.film}
          </span>
        )}
      </div>

      <div className="relative z-10">
        {member.experience && (
          <span className="inline-block text-[9px] uppercase tracking-overline text-black bg-[color:var(--dk-gold)] px-2 py-1 mb-3">
            {member.experience} · Experience
          </span>
        )}
        <h3 className={`font-serif-display leading-tight ${isTBA ? "text-white/50 italic text-xl" : "text-white text-2xl md:text-3xl"}`}>
          {member.name}
        </h3>
        <p className="text-[color:var(--dk-gold)]/90 text-sm mt-1 font-medium tracking-wide">{member.role}</p>

        {member.quote && (
          <p
            data-testid={`team-quote-${member.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="mt-4 font-serif-display italic text-white/90 text-base md:text-lg leading-snug border-l-2 border-[color:var(--dk-gold)]/60 pl-4"
          >
            “{member.quote}”
          </p>
        )}

        {member.bio && (
          <p className="mt-4 text-white/75 text-sm leading-relaxed line-clamp-6 md:line-clamp-none">
            {member.bio}
          </p>
        )}

        {member.ongoing && (
          <div className="mt-5 border-t border-white/15 pt-4" data-testid={`team-ongoing-${member.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-[9px] uppercase tracking-overline text-[color:var(--dk-gold)]/80 mb-3">
              Currently Working On
            </p>
            <ul className="space-y-2">
              {member.ongoing.map((slug) => {
                const f = FILMS.find((fl) => fl.slug === slug);
                if (!f) return null;
                return (
                  <li key={slug}>
                    <Link
                      to={`/films/${slug}`}
                      data-testid={`team-ongoing-link-${member.name.replace(/\s+/g, "-").toLowerCase()}-${slug}`}
                      className="flex items-baseline justify-between gap-3 group/proj"
                    >
                      <span className="font-serif-display italic text-white/85 text-sm leading-snug group-hover/proj:text-[color:var(--dk-gold)] transition-colors">{f.title}</span>
                      <span className="text-[9px] uppercase tracking-overline text-[color:var(--dk-gold)]/70 shrink-0">{f.status}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {member.email && (
          <a
            href={`mailto:${member.email}`}
            data-testid={`team-email-${member.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] eyebrow-link break-all"
          >
            <Mail size={12} /> {member.email}
          </a>
        )}
      </div>
    </Reveal>
  );
}
